import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { Printer, ArrowLeft, Phone, Mail, BedSingle, BedDouble, CalendarCheck, Users, MapPin } from "lucide-react";

const FLYER_IMAGE = "/shared-room-1.jpg";

const OFFERINGS = [
  { icon: BedSingle, text: "Private bedrooms in shared homes" },
  { icon: BedDouble, text: "Shared bedrooms (one bed, shared room)" },
  { icon: CalendarCheck, text: "Housing inquiries and tours" },
  { icon: Users, text: "Referrals from caseworkers & community organizations" },
];

export default function ReferralFlyer() {
  return (
    <div className="min-h-screen bg-[#F7F7F9] py-10 px-4 print:bg-white print:py-0 print:px-0" data-testid="referral-flyer-page">
      <Seo
        title="Referral Flyer | Shonta Solutions LLC — Shared Housing in Central Florida"
        description="Printable referral sheet for caseworkers and community organizations: shared housing in Central Florida, how to refer someone, and how to reach Shonta Solutions."
      />

      <div className="max-w-3xl mx-auto mb-6 flex items-center justify-between print:hidden">
        <Link to="/" data-testid="flyer-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-[#FF5A5F] hover:text-[#E0484D]">
          <ArrowLeft className="w-4 h-4" /> Back to website
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="flyer-print-button"
          className="inline-flex items-center gap-2 bg-[#FF5A5F] hover:bg-[#E0484D] text-white font-bold px-6 py-3 rounded-full transition-colors"
        >
          <Printer className="w-4 h-4" />
          Print / Save as PDF
        </button>
      </div>

      {/* Flyer sheet — sized to print on one letter page */}
      <div
        className="max-w-3xl mx-auto bg-white shadow-[0_16px_48px_rgba(0,0,0,0.12)] print:shadow-none print:max-w-none"
        data-testid="flyer-sheet"
      >
        <div className="bg-[#222222] text-white px-8 sm:px-12 py-8 flex items-center justify-between gap-6">
          <div>
            <p className="text-xs font-bold tracking-[0.25em] uppercase text-[#EDE9E1]">Referral Sheet</p>
            <h1 className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight leading-tight">
              Shared Housing in Central Florida
            </h1>
            <p className="mt-3 text-white/70 text-sm sm:text-base leading-relaxed max-w-md">
              Shonta Solutions provides shared housing for adults looking for a place to live —
              private and shared bedroom options, depending on availability.
            </p>
          </div>
          <span className="hidden sm:block bg-white rounded-2xl p-2 shrink-0">
            <img src="/logo-512.png" alt="Shonta Solutions LLC" className="h-20 w-auto" />
          </span>
        </div>

        <div className="px-8 sm:px-12 py-8">
          <div className="grid sm:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-lg font-bold text-[#222]">Who We Help</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-[#444]">
                Adults looking for a private or shared room, people going through a housing
                transition, and people looking for a more affordable place to live. We also work
                with families, caseworkers, and community organizations helping someone find housing.
              </p>

              <h2 className="mt-6 text-lg font-bold text-[#222]">What We Offer</h2>
              <ul className="mt-3 space-y-2.5">
                {OFFERINGS.map((o) => (
                  <li key={o.text} className="flex items-center gap-3 text-[14px] font-semibold text-[#333]">
                    <span className="w-8 h-8 rounded-lg bg-[#EDE9E1] flex items-center justify-center shrink-0">
                      <o.icon className="w-4 h-4 text-[#E0484D]" />
                    </span>
                    {o.text}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="rounded-2xl overflow-hidden">
                <img src={FLYER_IMAGE} alt="A simple shared bedroom with twin beds" className="w-full h-44 object-cover" />
              </div>
              <p className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-[#274B63]">
                <MapPin className="w-4 h-4 text-[#FF5A5F]" />
                Serving Central Florida, including the Orlando area
              </p>

              <div className="mt-5 bg-[#F7F7F9] border border-[#EBEBEB] rounded-2xl p-6">
                <h2 className="text-lg font-bold text-[#222]">How to Refer Someone</h2>
                <p className="mt-2 text-[14px] leading-relaxed text-[#444]">
                  Helping someone find a room? Contact us to ask what's open or to discuss a
                  referral. We'll let you know what's available and answer your questions.
                </p>
                <div className="mt-4 space-y-2.5">
                  <p className="flex items-center gap-3 text-[15px] font-bold text-[#222]">
                    <Phone className="w-4 h-4 text-[#FF5A5F]" /> Call or text: (561) 705-9807
                  </p>
                  <p className="flex items-center gap-3 text-[15px] font-bold text-[#222] break-all">
                    <Mail className="w-4 h-4 text-[#FF5A5F] shrink-0" /> shontasolutions@currently.com
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-5 border-t border-[#EBEBEB] flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-[#717171]">
            <span className="font-bold text-[#222]">Shonta Solutions LLC — Shared Housing in Central Florida</span>
            <span>Helping end homelessness, one room at a time.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
